import { makeObservable, observable, runInAction } from 'mobx'
import { addListItems, moveListItem, readEntityListItems, removeListItem } from 'app/lib/listApi'
import type { ContactJson } from 'app/types/entities'
import type { ListMembership } from 'app/types/list-memberships'
import { listIdsPlacedFor, PLACED_TTL_MS, placementsFor, recordPlacement, type ListPlacement } from 'lib/listPlacements'
import { logError } from 'lib/logging'

// A table re-mounts its rows on every drawer open, and each row asks again; within this window it is served from memory.
const REQUEST_FRESH_MS = 30_000

/**
 * Which practice lists each contact is on, for the surfaces that show one contact's lists. Writes land here at
 * once and in the placement overlay the list boards read, and a load lays that overlay over the search index's
 * answer, so a write shows everywhere before the index catches up.
 */
export class ListMembershipStore {
  byEntity = new Map<string, ListMembership[]>()
  // Set only by a successful load: a write also creates a `byEntity` entry, so that map cannot say what was read.
  private loadedAt = new Map<string, number>()
  private queued = new Set<string>()

  constructor() {
    makeObservable<this, `loadedAt`>(this, { byEntity: observable, loadedAt: observable })
  }

  membershipsOf(entityId: string): ListMembership[] {
    return this.byEntity.get(entityId) ?? []
  }

  isLoaded(entityId: string): boolean {
    return this.loadedAt.has(entityId)
  }

  async load(entityIds: readonly string[]): Promise<void> {
    if (entityIds.length === 0) return
    const items = await readEntityListItems(entityIds)
    const now = Date.now()
    const next = new Map<string, ListMembership[]>(entityIds.map((id) => [id, []]))
    for (const item of items) next.get(item.entityId)?.push({ listId: item.listId, stageId: item.stageId })
    for (const [entityId, memberships] of next) next.set(entityId, withPlacements(entityId, memberships, now))
    runInAction(() => {
      for (const [entityId, memberships] of next) {
        this.byEntity.set(entityId, memberships)
        this.loadedAt.set(entityId, now)
      }
    })
  }

  request(entityId: string): void {
    const loadedAt = this.loadedAt.get(entityId)
    if (loadedAt !== undefined && Date.now() - loadedAt < REQUEST_FRESH_MS) return
    const first = this.queued.size === 0
    this.queued.add(entityId)
    if (!first) return
    setTimeout(() => {
      const ids = [...this.queued]
      this.queued.clear()
      this.load(ids).catch((error) => logError(`Reading list memberships failed`, error))
    }, 0)
  }

  async move(listId: string, entity: ContactJson, stageId: string): Promise<void> {
    const entityId = entity.id!
    const before = this.membershipsOf(entityId)
    this.set(entityId, [...before.filter((m) => m.listId !== listId), { listId, stageId }])
    try {
      await moveListItem(listId, entityId, stageId)
      recordPlacement(listId, entityId, { stage: stageId, item: entity, at: Date.now() })
    } catch (error) {
      logError(`Writing a list membership failed, put back`, error)
      this.set(entityId, before)
      throw error
    }
  }

  async add(
    listId: string,
    entities: readonly ContactJson[],
    stageId: string,
  ): Promise<{ added: string[]; failed: string[] }> {
    const result = await addListItems(
      listId,
      entities.map((entity) => entity.id!),
      stageId,
    )
    const added = new Set(result.added)
    const now = Date.now()
    for (const entity of entities.filter((candidate) => added.has(candidate.id!))) {
      const entityId = entity.id!
      this.set(entityId, [...this.membershipsOf(entityId).filter((m) => m.listId !== listId), { listId, stageId }])
      recordPlacement(listId, entityId, { stage: stageId, item: entity, at: now })
    }
    return result
  }

  async remove(listId: string, entity: ContactJson): Promise<void> {
    const entityId = entity.id!
    const before = this.membershipsOf(entityId)
    this.set(
      entityId,
      before.filter((m) => m.listId !== listId),
    )
    try {
      await removeListItem(listId, entityId)
      recordPlacement(listId, entityId, { stage: null, item: entity, at: Date.now() })
    } catch (error) {
      logError(`Writing a list membership failed, put back`, error)
      this.set(entityId, before)
      throw error
    }
  }

  private set(entityId: string, memberships: ListMembership[]) {
    runInAction(() => this.byEntity.set(entityId, memberships))
  }
}

function withPlacements(entityId: string, read: ListMembership[], now: number): ListMembership[] {
  const placed = new Map<string, ListPlacement<unknown>>()
  for (const listId of listIdsPlacedFor(entityId)) {
    const placement = placementsFor(listId).get(entityId)!
    if (now - placement.at <= PLACED_TTL_MS) placed.set(listId, placement)
  }
  const kept = read.filter((m) => !placed.has(m.listId))
  const overlaid = [...placed].flatMap(([listId, placement]) =>
    placement.stage === null ? [] : [{ listId, stageId: placement.stage }],
  )
  return [...kept, ...overlaid]
}
